import React, { useState, useCallback } from 'react';
import VoucherContext from './VoucherContext';
import { voucherService } from '../../services/vouchers/voucherService';
import { getCachedVouchers, invalidateVouchersCache } from '../../services/vouchers/vouchersCache';
import { toast } from 'react-hot-toast';

export const VoucherState = ({ children }) => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const fetchVouchers = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      const data = await getCachedVouchers(forceRefresh);
      const list = Array.isArray(data) ? data : (data?.vouchers || []);
      setVouchers(list);
      return list;
    } catch (error) {
      console.error('Failed to fetch vouchers:', error);
      toast.error('Failed to load vouchers');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getVoucherById = useCallback(async (id) => {
    try {
      setLoading(true);
      const data = await voucherService.getVoucherById(id);
      setSelectedVoucher(data?.voucher || data);
      return data?.voucher || data;
    } catch (error) {
      console.error('Failed to fetch voucher details:', error);
      toast.error('Failed to load voucher details');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteVoucher = useCallback(async (voucherId) => {
    try {
      await voucherService.deleteVoucher(voucherId);
      invalidateVouchersCache();
      setVouchers((prev) => prev.filter((v) => v._id !== voucherId));
      toast.success('Voucher deleted successfully');
    } catch (error) {
      console.error('Failed to delete voucher:', error);
      toast.error(error.response?.data?.message || 'Failed to delete voucher');
      throw error;
    }
  }, []);

  const downloadVoucherPdf = useCallback(async (voucherId, type = 'download') => {
    try {
      return await voucherService.downloadVoucherPdf(voucherId, type);
    } catch (error) {
      console.error('Failed to download voucher PDF:', error);
      throw error;
    }
  }, []);

  const pingTraveller = useCallback(async (payload) => {
    try {
      const res = await voucherService.pingTraveller(payload);
      toast.success(res.message || 'Ping sent successfully');
      return res;
    } catch (error) {
      console.error('Failed to ping traveller:', error);
      toast.error(error.response?.data?.message || 'Failed to send ping');
      throw error;
    }
  }, []);

  const value = {
    vouchers,
    setVouchers,
    loading,
    selectedVoucher,
    setSelectedVoucher,
    fetchVouchers,
    getVoucherById,
    deleteVoucher,
    downloadVoucherPdf,
    pingTraveller,
    voucherService
  };

  return (
    <VoucherContext.Provider value={value}>
      {children}
    </VoucherContext.Provider>
  );
};

export default VoucherState;
